// HOC that controls "select with popover" behaviour, like:
// - opens popover when filter is typed or up/down/space is pressed
// - clears filter once closed
// - closes on value change or click (depends on closeOnValueChange prop)
// - controls `popoverPlacement` state
//
// Should be composed *before* withSelectController
//
// It's state can be controlled with `withPopoverSelectState`
//
// It expects PopoverSelectProps on the input and passes OutputPopoverSelectProps as output

import keycodes from 'keycodes'
import * as React from 'react'
import type {KeyboardEvent} from 'react'
import {compose, wrapDisplayName} from 'recompose'

import {autobind} from '@base/decorators'
import {createOrCallElement, omitProps} from '@base/react-utils'
import {withState} from '@base/react-utils/withState'

const isOpened = ({opened, disabled}: {opened?: boolean; disabled?: boolean}) =>
  opened && !disabled

const areAllItemsEqual = (
  nextItems: Array<any>,
  currentItems: Array<any>,
  {isItemEqual}
) => {
  if (nextItems === currentItems) return true
  if (!nextItems && !currentItems) return true
  if (!nextItems || !currentItems) return false
  const countMatches =
    (nextItems && nextItems.length) === (currentItems && currentItems.length)
  if (!countMatches) {
    return false
  }

  return nextItems.every((item, index) => {
    return isItemEqual(item, currentItems[index])
  })
}

type PopoverSelectProps = {
  disabled?: boolean
  opened?: boolean
  focused?: boolean
  filter?: string | undefined | null | any
  value?: string | Array<any>
  focusedIndex?: number
  addOnEnterKey?: boolean
  openOnMount?: boolean
  closeOnValueChange?: boolean
  resetFilterOnEscape?: boolean
  isItemEqual: (a: any, b: any) => boolean
  isFiltered: (filter: any) => boolean
  onFilterChange: (
    filter: string | undefined | null | any,
    options?: any
  ) => any
  onItemAdded: (filter: string | any) => void
  onItemToggle: (item: any) => void
  onValueChange: (value: any) => void
  onToggle: (opened: boolean) => void
  onKeyDown?: (event: KeyboardEvent) => void
  onFocus: Function
  onBlur: Function
}

export type OutputPopoverSelectProps = {
  opened: boolean
  focused: boolean
  addOnEnterKey: boolean
  focusedIndex: number | undefined | null
  onFocus: Function
  onBlur: Function
  popoverPlacement: string | undefined | null
  onPopoverPosition?: (position: any) => void
  onToggle: (opened: boolean) => void
  onFilterChange: (
    filter: string | undefined | null | any,
    options?: any
  ) => any
  onItemAdded: (filter: string | any) => void
  onItemToggle: (item: any) => void
  onValueChange: (value: any) => void
  onKeyDown: (event: KeyboardEvent) => void
  // below props are merged with any incoming props, sans closeOnValueChange
  closeOnValueChange?: void
}

type State = {
  popoverPlacement: string | undefined | null
}

const withPopoverSelect =
  ({
    multiple,
    selectIsTextInput = false,
  }: {
    multiple?: boolean
    selectIsTextInput?: boolean
  }) =>
  (Component: any) =>
    class PopoverSelectController extends React.PureComponent<
      PopoverSelectProps,
      State
    > {
      static displayName = wrapDisplayName(Component, 'withPopoverSelect')

      static defaultProps = {
        closeOnValueChange: !multiple,
        resetFilterOnEscape: true,
        isFiltered: (filter: any) => !!filter,
      }

      state = {
        popoverPlacement: null,
      }

      // @ts-ignore
      @autobind
      handleKeyDown(event: KeyboardEvent) {
        const {
          disabled,
          onKeyDown,
          opened,
          filter,
          isFiltered,
          resetFilterOnEscape,
          focusedIndex,
        } = this.props

        if (disabled) return

        const keyCode = event.keyCode
        const {defaultPrevented} = event

        if (keyCode === keycodes('esc') && !defaultPrevented && opened) {
          event.preventDefault()
          if (resetFilterOnEscape && isFiltered(filter)) {
            this.handleFilterChange(undefined, {immediate: true})
          } else {
            this.handleToggle(false)
          }
        } else if (
          (keyCode === keycodes('up') ||
            keyCode === keycodes('down') ||
            keyCode === keycodes('space')) &&
          !opened &&
          !selectIsTextInput
        ) {
          event.preventDefault()
          this.handleToggle(true)
        } else if (
          !defaultPrevented &&
          keyCode === keycodes('return') &&
          opened === true &&
          focusedIndex == null
        ) {
          // When user presses enter while popover is open, but no item was
          // selected, it means focus is in the input field, not the popover's
          // list. This happens e.g. when popover opens on click, but user types
          // in a value in TextInput, instead of selecting something in the
          // popover.
          // In such cases, just close the popover. Otherwise enter would
          // propagate and e.g. submit the form.
          this.handleToggle(false)
          event.preventDefault()
        }

        if (onKeyDown) onKeyDown(event)
      }

      // @ts-ignore
      @autobind
      handleItemToggle(item: any) {
        const {onItemToggle, closeOnValueChange, opened} = this.props

        // in single select, toggling an item closes popover
        if (!multiple && closeOnValueChange && opened) {
          this.handleToggle(false)
        }

        if (onItemToggle) onItemToggle(item)
      }

      // @ts-ignore
      @autobind
      handleItemAdded(item: any) {
        const {onItemAdded, closeOnValueChange, opened} = this.props

        // adding an item closes popover
        if (closeOnValueChange && opened) {
          this.handleToggle(false)
        }

        if (onItemAdded) onItemAdded(item)
      }

      // @ts-ignore
      @autobind
      handleValueChange(value?: string | undefined | null | Array<any>) {
        const {onValueChange, closeOnValueChange, opened} = this.props

        // in multi select, changing the value closes popover
        if (
          multiple &&
          closeOnValueChange &&
          opened &&
          this.props.value instanceof Array &&
          value instanceof Array &&
          !areAllItemsEqual(this.props.value, value, this.props)
        ) {
          this.handleToggle(false)
        }

        if (onValueChange) onValueChange(value)
      }

      // @ts-ignore
      @autobind
      handlePopoverPosition(position: any) {
        const {placement: popoverPlacement} = position
        if (popoverPlacement !== this.state.popoverPlacement) {
          this.setState({popoverPlacement})
        }
      }

      // @ts-ignore
      @autobind
      handleToggle(opened: boolean) {
        const {onToggle} = this.props
        onToggle(opened)
      }

      // @ts-ignore
      @autobind
      handleFilterChange(filter: any, options: any) {
        const {onFilterChange, opened, isFiltered} = this.props
        if (!opened && isFiltered(filter)) {
          this.handleToggle(true)
        }
        if (onFilterChange) onFilterChange(filter, options)
      }

      render() {
        const {props} = this
        const {addOnEnterKey, focusedIndex} = props
        const {popoverPlacement} = this.state
        const opened = isOpened(props)
        const outProps: OutputPopoverSelectProps = {
          ...omitProps(props, ['closeOnValueChange']),
          opened,
          focused: props.focused || opened,
          // unless opened, toggling or adding items is disabled
          addOnEnterKey: opened ? addOnEnterKey : false,
          focusedIndex: opened ? focusedIndex : null,
          onFocus: props.onFocus,
          onBlur: props.onBlur,
          popoverPlacement,
          onPopoverPosition: this.handlePopoverPosition,

          onToggle: this.handleToggle,
          onFilterChange: this.handleFilterChange,
          onItemToggle: this.handleItemToggle,
          onItemAdded: this.handleItemAdded,
          onValueChange: this.handleValueChange,
          onKeyDown: this.handleKeyDown,
        }
        return createOrCallElement(Component, outProps)
      }
    }

export const withPopoverSelectState = () =>
  compose(
    withState({
      prop: 'opened',
      onChangeProp: 'onToggle',
      mapInitialValue: ({openOnMount}) => !!openOnMount,
    })
  )

export default withPopoverSelect
